import { supabase } from '../supabase';
import {
  useQuery,
  useMutation,
  useQueryClient
} from '@tanstack/react-query';
import { ModelFormat } from '@shiftly/shared/src/schema';
import { useScheduleShifts } from '../schedule-shifts/supabaseScheduleShiftService';
import { useAuth } from '../../../contexts/AuthContext';
import { Permissions } from '@shiftly/shared/src/permissions';
import { DateTime } from 'luxon';

class SupabaseModelFormatService {
  async getModelFormat(model_id: string): Promise<ModelFormat | null> {
    const { data, error } = await supabase
      .from('v_model_formats_with_items')
      .select('data')
      .eq('model_id', model_id)
      .maybeSingle();

    if (error) {
      if (error.code === 'PGRST116') { // Not found
        return null;
      }
      throw new Error(`Failed to get model format: ${error.message}`);
    }

    if (!data?.data) return null;

    const formatData = data.data;
    return {
      id: formatData.id,
      model_id: formatData.model_id,
      items: formatData.items || [],
      created_at: DateTime.fromISO(formatData.created_at, { zone: 'UTC' }),
      updated_at: DateTime.fromISO(formatData.updated_at, { zone: 'UTC' }),
    } as ModelFormat;
  }

  async getAllModelFormats(): Promise<ModelFormat[]> {
    const { data, error } = await supabase
      .from('v_model_formats_with_items')
      .select('data');

    if (error) {
      throw new Error(`Failed to get all model formats: ${error.message}`);
    }

    return (data || []).map(row => {
      const formatData = row.data;
      return {
        id: formatData.id,
        model_id: formatData.model_id,
        items: formatData.items || [],
        created_at: DateTime.fromISO(formatData.created_at, { zone: 'UTC' }),
        updated_at: DateTime.fromISO(formatData.updated_at, { zone: 'UTC' }),
      };
    });
  }

  async updateModelFormat(model_id: string, format: Partial<ModelFormat>) {
    // Check if format exists
    const currentFormat = await this.getModelFormat(model_id);
    if (!currentFormat) {
      // If no format exists, create one
      await this.createModelFormat(model_id, {
        model_id: model_id,
        items: format.items || []
      });
      return;
    }

    // Start a transaction
    const { error: formatError } = await supabase
      .from('model_formats')
      .update({
        updated_at: DateTime.utc()
      })
      .eq('model_id', model_id);

    if (formatError) {
      throw new Error(`Failed to update model format: ${formatError.message}`);
    }

    // If items are provided, update them
    if (format.items) {
      // Delete existing items
      const { error: deleteError } = await supabase
        .from('model_format_items')
        .delete()
        .eq('format_id', currentFormat.id);

      if (deleteError) {
        throw new Error(`Failed to delete existing format items: ${deleteError.message}`);
      }

      // Insert new items
      if (format.items.length > 0) {
        const itemsToInsert = format.items.map((item, index) => ({
          format_id: currentFormat.id,
          position: index,
          time: item.time,
          type: item.type,
          content: item.content || null,
          group: item.group || null,
          model: item.model || null,
          model_id: item.model_id || null,
          launch_group: item.launch_group || null,
          am: item.am || null,
          pm: item.pm || null
        }));

        const { error: insertError } = await supabase
          .from('model_format_items')
          .insert(itemsToInsert);

        if (insertError) {
          throw new Error(`Failed to insert new format items: ${insertError.message}`);
        }
      }
    }
  }

  async createModelFormat(model_id: string, format: Omit<ModelFormat, 'id' | 'created_at' | 'updated_at'>) {
    // Insert the format
    const { data: formatData, error: formatError } = await supabase
      .from('model_formats')
      .insert({
        model_id,
        created_at: DateTime.utc(),
        updated_at: DateTime.utc()
      })
      .select()
      .single();

    if (formatError) {
      throw new Error(`Failed to create model format: ${formatError.message}`);
    }

    // Insert format items if provided
    if (format.items && format.items.length > 0) {
      const itemsToInsert = format.items.map((item, index) => ({
        format_id: formatData.id,
        position: index,
        time: item.time,
        type: item.type,
        content: item.content || null,
        group: item.group || null,
        model: item.model || null,
        model_id: item.model_id || null,
        launch_group: item.launch_group || null,
        am: item.am || null,
        pm: item.pm || null
      }));

      const { error: itemsError } = await supabase
        .from('model_format_items')
        .insert(itemsToInsert);

      if (itemsError) {
        throw new Error(`Failed to create format items: ${itemsError.message}`);
      }
    }

    return formatData.id;
  }
}

const instance = new SupabaseModelFormatService();

// Custom hook to check if user can edit reset timer
export const useCanEditResetTimer = (user_id: string, permissions: string[], model_id: string) => {
  const hasAdminPermission = permissions.includes(Permissions.SCHEDULE_EDIT);
  if (hasAdminPermission) return true;

  const today = DateTime.now().setZone('Europe/London');

  const startDate = today.startOf('day');
  const endDate = today.endOf('day');

  const filters = [
    { field: 'user_id', operator: 'eq' as const, value: user_id },
    { field: 'model_id', operator: 'eq' as const, value: model_id },
    { field: 'type', operator: 'eq' as const, value: 'MASS_DM' },
    { field: 'date', operator: 'gte' as const, value: startDate.toISO() },
    { field: 'date', operator: 'lte' as const, value: endDate.toISO() }
  ];

  const { data: todayShifts = [] } = useScheduleShifts({
    filters
  });

  const fpFilters = [
    { field: 'user_id', operator: 'eq' as const, value: user_id },
    { field: 'model_id', operator: 'eq' as const, value: model_id },
    { field: 'type', operator: 'eq' as const, value: 'FP_MASS_DM' },
    { field: 'date', operator: 'gte' as const, value: startDate.toISO() },
    { field: 'date', operator: 'lte' as const, value: endDate.toISO() }
  ];

  const { data: fpTodayShifts = [] } = useScheduleShifts({
    filters: fpFilters
  });

  const hasEditPermission = permissions.includes(Permissions.SCHEDULE_EDIT_RESET_TIMER);
  const hasMassDMShiftToday = todayShifts.length > 0 || fpTodayShifts.length > 0;

  return hasEditPermission && hasMassDMShiftToday;
};

// Custom hook to check if user can edit format
export const useCanEditFormat = () => {
  const { permissions } = useAuth();
  return permissions.includes(Permissions.SCHEDULE_EDIT);
};

// React Query Hooks
export const useModelFormat = (model_id: string) => {
  return useQuery({
    queryKey: ['modelFormats', model_id],
    queryFn: () => instance.getModelFormat(model_id),
    enabled: !!model_id,
  });
};

export const useAllModelFormats = () => {
  return useQuery({
    queryKey: ['allModelFormats'],
    queryFn: () => instance.getAllModelFormats(),
    staleTime: Infinity, // Cache until page refresh
    gcTime: Infinity, // Don't garbage collect
  });
};

export const useUpdateModelFormat = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ model_id, format }: { model_id: string; format: Partial<ModelFormat> }) =>
      instance.updateModelFormat(model_id, format),
    onSuccess: (_, { model_id }) => {
      queryClient.invalidateQueries({ queryKey: ['modelFormats', model_id] });
      queryClient.invalidateQueries({ queryKey: ['allModelFormats'] });
    },
  });
};

export const useCreateModelFormat = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ model_id, format }: { model_id: string; format: Omit<ModelFormat, 'id' | 'created_at' | 'updated_at'> }) =>
      instance.createModelFormat(model_id, format),
    onSuccess: (_, { model_id }) => {
      queryClient.invalidateQueries({ queryKey: ['modelFormats', model_id] });
      queryClient.invalidateQueries({ queryKey: ['allModelFormats'] });
    },
  });
};

export const useCanToggleFormat = (user_id: string, permissions: string[], model_id: string) => {
  const hasAdminPermission = permissions.includes(Permissions.SCHEDULE_EDIT);
  if (hasAdminPermission) return true;

  const today = DateTime.now().setZone('Europe/London');

  const startDate = today.startOf('day');
  const endDate = today.endOf('day');

  const filters = [
    { field: 'user_id', operator: 'eq' as const, value: user_id },
    { field: 'model_id', operator: 'eq' as const, value: model_id },
    { field: 'type', operator: 'eq' as const, value: 'MASS_DM' },
    { field: 'date', operator: 'gte' as const, value: startDate.toISO() },
    { field: 'date', operator: 'lte' as const, value: endDate.toISO() }
  ];

  const { data: todayShifts = [] } = useScheduleShifts({
    filters
  });

  const fpFilters = [
    { field: 'user_id', operator: 'eq' as const, value: user_id },
    { field: 'model_id', operator: 'eq' as const, value: model_id },
    { field: 'type', operator: 'eq' as const, value: 'FP_MASS_DM' },
    { field: 'date', operator: 'gte' as const, value: startDate.toISO() },
    { field: 'date', operator: 'lte' as const, value: endDate.toISO() }
  ];

  const { data: fpTodayShifts = [] } = useScheduleShifts({
    filters: fpFilters
  });

  const hasTogglePermission = permissions.includes(Permissions.SCHEDULE_EDIT_RESET_TIMER);
  const hasMassDMShiftToday = todayShifts.length > 0 || fpTodayShifts.length > 0;

  return hasTogglePermission && hasMassDMShiftToday;
};

export default instance;